<template>
    <Head title="Tickets" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Tickets</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Track and manage support tickets</p>
            </div>
            <div class="flex items-center space-x-3">
                <Link
                    :href="route('tickets.calendar')"
                    class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Calendar
                </Link>
                <Link
                    v-if="canCreate"
                    :href="route('tickets.create')"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New Ticket
                </Link>
            </div>
        </div>

        <!-- View Tabs -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 border border-gray-200 dark:border-gray-700">
            <div class="border-b border-gray-200 dark:border-gray-700">
                <nav class="-mb-px flex space-x-8 px-6">
                    <button
                        v-for="viewOption in viewOptions"
                        :key="viewOption.value"
                        @click="changeView(viewOption.value)"
                        :class="[
                            'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
                            view === viewOption.value
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        ]"
                    >
                        {{ viewOption.label }}
                        <span
                            v-if="viewOption.count !== undefined"
                            :class="[
                                'ml-2 py-0.5 px-2 rounded-full text-xs',
                                view === viewOption.value
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            ]"
                        >
                            {{ viewOption.count }}
                        </span>
                    </button>
                </nav>
            </div>
        </div>

        <!-- Filters and Search -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 border border-gray-200 dark:border-gray-700">
            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <!-- Search -->
                    <div class="flex-1 max-w-lg">
                        <div class="relative">
                            <input
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search tickets..."
                                class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                @input="debounceSearch"
                            />
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg class="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- Bulk Actions -->
                    <div v-if="canBulkSelect && selectedTickets.length > 0" class="flex items-center space-x-2">
                        <span class="text-sm text-gray-700 dark:text-gray-300">
                            {{ selectedTickets.length }} selected
                        </span>
                        <button
                            @click="clearSelection"
                            class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            <!-- Filter Pills -->
            <div class="p-4 flex flex-wrap gap-2">
                <select
                    v-model="selectedFilters.status"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pending">Pending</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                    <option value="cancelled">Cancelled</option>
                </select>

                <select
                    v-model="selectedFilters.priority"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                </select>

                <select
                    v-model="selectedFilters.category_id"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Categories</option>
                    <option v-for="category in categories" :key="category.id" :value="category.id">
                        {{ category.name }}
                    </option>
                </select>

                <select
                    v-model="selectedFilters.assignee_id"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Assignees</option>
                    <option v-for="user in users" :key="user.id" :value="user.id">
                        {{ user.full_name }}
                    </option>
                </select>

                <button
                    v-if="hasActiveFilters"
                    @click="clearFilters"
                    class="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                >
                    Clear Filters
                </button>
            </div>
        </div>

        <!-- Tickets Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-6 border border-gray-200 dark:border-gray-700">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th v-if="canBulkSelect" scope="col" class="w-12 px-6 py-3">
                            <input
                                type="checkbox"
                                :checked="allSelected"
                                @change="toggleSelectAll"
                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Ticket
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Category
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Priority
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <div class="flex items-center gap-2">
                                SLA Status
                                <InfoButton title="SLA Status Indicators" variant="compact">
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">SLA Status Indicators</strong><br>
                                        • <strong>On Track (Green):</strong> 50%+ time remaining<br>
                                        • <strong>Warning (Yellow):</strong> 20-49% time remaining<br>
                                        • <strong>Critical (Orange):</strong> Less than 20% time remaining<br>
                                        • <strong>Breached (Red):</strong> Deadline passed<br>
                                        • <strong>Met (Blue):</strong> Completed within SLA<br>
                                        • <strong>Breached (Met) (Purple):</strong> Completed but SLA was breached
                                    </p>
                                </InfoButton>
                            </div>
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Assigned To
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <button
                                @click="sortBy('created_at')"
                                class="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                Request Date
                                <svg
                                    v-if="sortField === 'created_at' && sortDirection === 'asc'"
                                    class="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                                </svg>
                                <svg
                                    v-else-if="sortField === 'created_at' && sortDirection === 'desc'"
                                    class="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                </svg>
                                <svg
                                    v-else
                                    class="w-4 h-4 text-gray-400 dark:text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </button>
                        </th>
                        <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr
                        v-for="ticket in tickets.data"
                        :key="ticket.id"
                        :class="[
                            'hover:bg-gray-50 dark:hover:bg-gray-700',
                            selectedTickets.includes(ticket.id) ? 'bg-blue-50 dark:bg-blue-900/20' : '',
                            ticket.is_overdue ? 'bg-red-50 dark:bg-red-900/20' : ''
                        ]"
                    >
                        <td v-if="canBulkSelect" class="px-6 py-4 whitespace-nowrap">
                            <input
                                type="checkbox"
                                :checked="selectedTickets.includes(ticket.id)"
                                @change="toggleSelect(ticket.id)"
                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                        </td>
                        <td class="px-6 py-4">
                            <Link
                                :href="route('tickets.show', ticket.id)"
                                class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                            >
                                {{ ticket.ticket_number }}
                            </Link>
                            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">{{ ticket.subject }}</p>
                            <p v-if="ticket.is_overdue" class="text-xs text-red-600 dark:text-red-400 font-medium mt-1">Overdue</p>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <span v-if="ticket.category">{{ ticket.category.name }}</span>
                            <span v-else class="text-gray-400 dark:text-gray-500">-</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span
                                :class="[
                                    'px-2 py-1 text-xs font-medium rounded-full',
                                    getPriorityClass(ticket.priority)
                                ]"
                            >
                                {{ ticket.priority_label }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span
                                :class="[
                                    'px-2 py-1 text-xs font-medium rounded-full',
                                    getStatusClass(ticket.status)
                                ]"
                            >
                                {{ ticket.status_label }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <span
                                v-if="ticket.sla_policy_id"
                                :class="[
                                    'px-2 py-1 text-xs font-medium rounded-full',
                                    getSlaStatusClass(ticket.sla_status)
                                ]"
                            >
                                {{ getSlaStatusLabel(ticket.sla_status) }}
                            </span>
                            <span v-else class="text-xs text-gray-400 dark:text-gray-500">No SLA</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                            <span v-if="ticket.assigned_to_user" class="text-gray-900 dark:text-white">
                                {{ ticket.assigned_to_user.full_name }}
                            </span>
                            <span v-else class="text-gray-400 dark:text-gray-500">Unassigned</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {{ formatDateOnly(ticket.created_at) }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                                :href="route('tickets.show', ticket.id)"
                                class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                            >
                                View
                            </Link>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div v-if="tickets.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-lg shadow">
            <div class="flex-1 flex justify-between sm:hidden">
                <Link
                    v-if="tickets.prev_page_url"
                    :href="tickets.prev_page_url"
                    class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Previous
                </Link>
                <Link
                    v-if="tickets.next_page_url"
                    :href="tickets.next_page_url"
                    class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Next
                </Link>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                    <p class="text-sm text-gray-700 dark:text-gray-300">
                        Showing
                        <span class="font-medium">{{ tickets.from }}</span>
                        to
                        <span class="font-medium">{{ tickets.to }}</span>
                        of
                        <span class="font-medium">{{ tickets.total }}</span>
                        results
                    </p>
                </div>
                <div>
                    <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <template v-for="link in tickets.links" :key="link.label">
                            <Link
                                v-if="link.url"
                                :href="link.url"
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    link.active
                                        ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400'
                                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                ]"
                                v-html="link.label"
                            />
                            <span
                                v-else
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium cursor-not-allowed opacity-50',
                                    'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                                ]"
                                v-html="link.label"
                            />
                        </template>
                    </nav>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-if="tickets.data.length === 0" class="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center border border-gray-200 dark:border-gray-700">
            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No tickets found</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new ticket.</p>
            <div class="mt-6">
                <Link
                    v-if="canCreate"
                    :href="route('tickets.create')"
                    class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New Ticket
                </Link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    tickets: Object,
    categories: Array,
    users: Array,
    view: String,
    filters: Object,
    canViewAll: Boolean,
    canEdit: Boolean,
    canAssign: Boolean,
    canDelete: Boolean,
    canCreate: Boolean,
    canSeeAllTicketsView: Boolean,
});

// Use timezone composable for date formatting
const { formatDate: formatDateInTZ } = useTimezone();

// Computed property to check if user can perform bulk operations
const canBulkSelect = computed(() => props.canEdit || props.canAssign);

const searchQuery = ref(props.filters.search || '');
const selectedFilters = ref({
    status: props.filters.status || '',
    priority: props.filters.priority || '',
    category_id: props.filters.category_id || '',
    assignee_id: props.filters.assignee_id || '',
});
const selectedTickets = ref([]);
const sortField = ref(props.filters.sort_by || '');
const sortDirection = ref(props.filters.sort_direction || 'desc');

// Filter view options based on permissions
const viewOptions = computed(() => {
    const options = [];

    // "All Tickets" tab - only show if user has view_all OR has assigned tickets
    if (props.canSeeAllTicketsView) {
        options.push({ value: 'all', label: 'All Tickets' });
    }

    // "My Tickets" tab - always visible
    options.push({ value: 'my', label: 'My Tickets' });

    // "Unassigned" tab - only show if user can assign tickets
    if (props.canAssign) {
        options.push({ value: 'unassigned', label: 'Unassigned' });
    }

    // "Reported by Me" tab - always visible
    options.push({ value: 'reported', label: 'Reported by Me' });

    return options;
});

const allSelected = computed(() => {
    return props.tickets.data.length > 0 &&
           selectedTickets.value.length === props.tickets.data.length;
});

const hasActiveFilters = computed(() => {
    return selectedFilters.value.status ||
           selectedFilters.value.priority ||
           selectedFilters.value.category_id ||
           selectedFilters.value.assignee_id ||
           searchQuery.value ||
           sortField.value;
});

let searchTimeout = null;

const debounceSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const changeView = (newView) => {
    router.get(route('tickets.index'), {
        view: newView,
        search: searchQuery.value,
        ...selectedFilters.value,
        sort_by: sortField.value,
        sort_direction: sortDirection.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const applyFilters = () => {
    router.get(route('tickets.index'), {
        view: props.view,
        search: searchQuery.value,
        ...selectedFilters.value,
        sort_by: sortField.value,
        sort_direction: sortDirection.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const sortBy = (field) => {
    if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortField.value = field;
        sortDirection.value = 'desc';
    }
    applyFilters();
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedFilters.value = {
        status: '',
        priority: '',
        category_id: '',
        assignee_id: '',
    };
    sortField.value = '';
    sortDirection.value = 'desc';
    applyFilters();
};

const toggleSelect = (id) => {
    const index = selectedTickets.value.indexOf(id);
    if (index > -1) {
        selectedTickets.value.splice(index, 1);
    } else {
        selectedTickets.value.push(id);
    }
};

const toggleSelectAll = () => {
    if (allSelected.value) {
        selectedTickets.value = [];
    } else {
        selectedTickets.value = props.tickets.data.map(t => t.id);
    }
};

const clearSelection = () => {
    selectedTickets.value = [];
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        normal: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getStatusClass = (status) => {
    const classes = {
        open: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        in_progress: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        pending: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        resolved: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        closed: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

// Format date in company timezone
const formatDate = (date) => {
    return formatDateInTZ(date); // Now timezone-aware using company timezone!
};

// Format date only (no time) in company timezone
const formatDateOnly = (date) => {
    if (!date) return '-';
    return formatDateInTZ(date); // Now timezone-aware using company timezone!
};

const getSlaStatusClass = (status) => {
    const classes = {
        on_track: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        critical: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        breached: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        met: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        breached_met: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getSlaStatusLabel = (status) => {
    const labels = {
        on_track: 'On Track',
        warning: 'Warning',
        critical: 'Critical',
        breached: 'Breached',
        met: 'Met',
        breached_met: 'Breached (Met)',
    };
    return labels[status] || status;
};
</script>
